import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { APIService } from 'app/services/api.service';
import { environment } from 'environments/environment';
import { Store } from "@ngrx/store";
import { selectTokenInfo } from "app/reducers/context.selectors";
import { TokenInfo } from "app/reducers/context.reducer";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { refreshLoginData, signOutUser } from "app/reducers/context.actions";
import { InterceptorHelper } from 'app/services/http/interceptor-helper';
import { ApiAuthRefreshUrl, ApiSignalUrl } from 'app/services/http/url-constants/hub-url-constants';

const Unauthenticated = "Unauthenticated"

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class TokenInterceptorService implements HttpInterceptor {
  private tokenInfo: TokenInfo | null = null;

  constructor(
    private apiService: APIService,
    private store: Store,
  ) {
    this.store.select(selectTokenInfo).pipe(
      untilDestroyed(this)
    ).subscribe(tokenInfo => {
      this.tokenInfo = tokenInfo
    })
  }

  intercept(
    req: HttpRequest<object>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if ((req.url.includes(environment.baseUrl) || req.url.includes(environment.posBaseUrl) || req.url.includes(environment.kioskBaseUrl) || req.url.includes(environment.kioskVPNBaseUrl) || req.url.includes(environment.kioskMonitoringBaseUrl))
      && req.url.indexOf(ApiSignalUrl) === -1)
    {
      if (this.tokenInfo) {
        req = this.addToken(req, this.tokenInfo.token);
      }
    }

    return next.handle(req).pipe(
      catchError((err, event) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          if (err.url.includes(ApiAuthRefreshUrl)) {
            throw new Error(Unauthenticated);
          }
          return this.refreshTokenHandle(req, next);
        }
        throw err;
      }),
      switchMap((event) => {
        if (event instanceof HttpResponse) {
          if (InterceptorHelper.IsJsonRpc(event) && InterceptorHelper.NoAuthorizeJsonRpc(event)) {
            return this.refreshTokenHandle(req, next);
          }
        }
        return of(event);
      }),
      catchError((err, event) => {
        if (err.message == Unauthenticated) {
          this.store.dispatch(signOutUser());
        }
        throw err;
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({setHeaders: {Authorization: `Bearer ${token}`}});
  }

  private refreshTokenHandle(
    req: HttpRequest<object>,
    next: HttpHandler
  ): Observable<HttpEvent<any>>
  {
    if (!this.tokenInfo?.refreshToken){
      throw new Error(Unauthenticated);
    }

    return this.apiService.refreshToken({
      refreshToken: this.tokenInfo.refreshToken
    }).pipe(
      switchMap(loginResponse => {
        if (!loginResponse?.data?.refreshToken){
          throw new Error(Unauthenticated);
        }

        const newToken = loginResponse.data.authToken;
        const tokenParts = this.apiService.decodeToken(loginResponse.data.authToken);

        this.store.dispatch(refreshLoginData({
          tokenInfo: {
            token: loginResponse.data.authToken,
            refreshToken: loginResponse.data.refreshToken,
            tokenExpires: tokenParts.tokenExpires,
          },
          userData: loginResponse.data.userData
        }));
        return next.handle(this.addToken(req, newToken));
      }),
      catchError(err => {
        throw new Error(Unauthenticated)
      })
    )
  }
}
